import { Controller } from "stimulus";

const REVEAL_DELAY_MS = 1500;
const ENTER_MS = 3000;
const EXIT_MS = 700;
const FADE_MS = 160;

// One transient, non-interactive notice, revealed when its host is in view after page layout settles.
// Usage: data-controller="interest-notice" on a positioned host containing a template target.
// The template's [data-interest-notice-digits] text becomes split-flap digits; duration-value is the hold in ms.
// Emits interest-notice:shown and interest-notice:dismissed; disconnect removes the overlay and cancels work.
export default class extends Controller {
  static targets = ["template"];
  static values = { duration: Number };

  connect() {
    if (this.played || !this.hasTemplateTarget) return;
    this.timers = [];
    const connection = {};
    this.connection = connection;
    const loaded = document.readyState === "complete" ? Promise.resolve() : new Promise(resolve => {
      this.onLoad = resolve;
      window.addEventListener("load", this.onLoad, { once: true });
    });
    loaded.then(() => document.fonts.ready).then(() => {
      if (this.connection !== connection) return;
      this.frame = requestAnimationFrame(() => this.watchForView());
    });
  }

  disconnect() {
    this.connection = null;
    window.removeEventListener("load", this.onLoad);
    cancelAnimationFrame(this.frame);
    if (this.observer) this.observer.disconnect();
    this.observer = null;
    (this.timers || []).forEach(timer => clearTimeout(timer));
    if (this.overlay) this.overlay.remove();
    this.overlay = null;
  }

  watchForView() {
    if (!("IntersectionObserver" in window)) {
      this.schedule(() => this.reveal(), REVEAL_DELAY_MS);
      return;
    }
    this.observer = new IntersectionObserver(entries => {
      const visible = entries.some(entry => entry.isIntersecting &&
        entry.intersectionRect.height >= Math.min(entry.boundingClientRect.height, innerHeight) * 0.55);
      if (!visible) return;
      this.observer.disconnect();
      this.observer = null;
      this.schedule(() => this.reveal(), REVEAL_DELAY_MS);
    }, { threshold: Array.from({ length: 11 }, (_, index) => index / 10) });
    this.observer.observe(this.element);
  }

  reveal() {
    if (this.played) return;
    this.played = true;
    this.overlay = this.templateTarget.content.firstElementChild.cloneNode(true);
    const digits = this.overlay.querySelector("[data-interest-notice-digits]");
    const characters = Array.from(digits.textContent.trim());
    digits.textContent = "";
    characters.forEach((character, index) => {
      const flap = document.createElement("span");
      flap.className = "interest-notice__flap";
      flap.textContent = character;
      flap.style.setProperty("--interest-delay", 260 + index * 130 + "ms");
      flap.style.setProperty("--interest-out-delay", (characters.length - 1 - index) * 40 + "ms");
      digits.appendChild(flap);
    });
    this.reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.overlay.classList.toggle("is-reduced", this.reduced);
    // The overlay fills the padding box; lift it by the top border so the plate rests on the border's outer
    // edge instead of covering a badge drawn over that border.
    this.overlay.style.top = -this.element.clientTop + "px";
    this.element.appendChild(this.overlay);
    this.overlay.getBoundingClientRect();
    this.frame = requestAnimationFrame(() => this.enter());
    // Background tabs can throttle frames. The guard in enter prevents a double start.
    this.schedule(() => this.enter(), 120);
  }

  enter() {
    if (!this.overlay || this.overlay.classList.contains("is-in")) return;
    this.overlay.classList.add("is-in");
    this.schedule(() => {
      this.emit("shown");
      this.schedule(() => this.dismiss(), this.durationValue > 0 ? this.durationValue : 5000);
    }, this.reduced ? FADE_MS : ENTER_MS);
  }

  dismiss() {
    this.overlay.classList.add("is-out");
    this.schedule(() => {
      this.overlay.remove();
      this.overlay = null;
      this.emit("dismissed");
    }, this.reduced ? FADE_MS : EXIT_MS);
  }

  schedule(callback, delay) {
    this.timers.push(setTimeout(callback, delay));
  }

  emit(name) {
    this.element.dispatchEvent(new CustomEvent("interest-notice:" + name, { bubbles: true }));
  }
}
